import {MEDIUM_BREAKPOINT} from '@wandb/weave/common/css/globals.styles';
import React, {memo, useEffect, useState} from 'react';
import {useHistory, useParams} from 'react-router';

import {Content} from '../../components/PageWithSidebar/PageWithSidebar.styles';
import {hasTraceBackend} from '../../config';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import * as Track from '../../services/analytics';
import {useViewer} from '../../state/viewer/hooks';
import {Viewer} from '../../state/viewer/types';
import * as S from './QuickStart.styles';
import {QuickStartModels} from './QuickStartModels';
import {Product, QuickStartProductSelect} from './QuickStartProductSelect';
import {QuickStartWeave} from './QuickStartWeave';
import {QuickStartAnalyticsOrigin} from './types';

const DEFAULT_TABLET_OFFSET = 350; // Sidebar in quickstart and team page
const PROJECT_PAGE_TABLET_OFFSET = 100;

type QuickStartProps = {
  analyticsOrigin: QuickStartAnalyticsOrigin; // defaults to quickstart on home page
};

const QuickStartComp = ({
  analyticsOrigin = QuickStartAnalyticsOrigin.QUICKSTART_PAGE,
}: QuickStartProps) => {
  const viewer: Viewer | undefined = useViewer();
  const defaultEntityName = viewer?.defaultEntity?.name ?? '';

  const params = useParams<{entityName?: string; projectName?: string}>();
  const entityName = params.entityName ?? defaultEntityName;
  const projectName = params.projectName ?? '';

  // Tablet width + sidebar offsets depending on which page this comp is rendered in
  const maxWidth =
    MEDIUM_BREAKPOINT +
    (analyticsOrigin === QuickStartAnalyticsOrigin.PROJECT_PAGE
      ? PROJECT_PAGE_TABLET_OFFSET
      : DEFAULT_TABLET_OFFSET);

  const history = useHistory();
  const userInfoProduct = viewer?.userInfo?.wandbProductType;
  const searchParams = new URLSearchParams(window.location.search);
  const searchParamProduct = searchParams.get('product');

  const productString = searchParamProduct ?? userInfoProduct;
  // default to weave unless models is specified or no weave available
  const product =
    productString === Product.Models || !hasTraceBackend
      ? Product.Models
      : Product.Weave;
  const onSelectProduct = (newProduct: Product) => {
    searchParams.set('product', newProduct);
    history.replace({search: searchParams.toString()});
  };

  // If the user clicks over to weave, then both view events have been sent and we dont want to send any more events
  // So this state is used to track that

  // TODO(hhuang): probably just use a map here and not rely on defaults
  const [bothEventsSent, setBothEventsSent] = useState(false);
  useEffect(() => {
    if (bothEventsSent) {
      return;
    }
    Track.quickStartViewed({
      projectName,
      entityName,
      location: analyticsOrigin,
      quickstartType: product,
    });
    if (product === Product.Models) {
      setBothEventsSent(true);
    }
  }, [analyticsOrigin, bothEventsSent, entityName, product, projectName]);

  return (
    <Content background="white">
      <S.QuickStartContainer $maxWidth={maxWidth} data-test="quickstart">
        <S.QuickStart>
          {hasTraceBackend && (
            <QuickStartProductSelect
              selected={product}
              onSelectProduct={onSelectProduct}
            />
          )}
          {product === Product.Models ? (
            <QuickStartModels
              entityName={entityName}
              projectName={projectName}
              analyticsOrigin={analyticsOrigin}
              maxWidth={maxWidth}
            />
          ) : (
            <QuickStartWeave
              entityName={entityName}
              projectName={projectName}
              analyticsOrigin={analyticsOrigin}
              maxWidth={maxWidth}
            />
          )}
        </S.QuickStart>
      </S.QuickStartContainer>
    </Content>
  );
};

export const QuickStart = memo(QuickStartComp);
export default QuickStart;
